import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Search } from 'lucide-react'
import DropdownOptionsGrid from '@/components/DropdownOptions/DropdownOptionsGrid'
import { DropdownOptionDialog } from '@/components/DropdownOptions/DropdownOptionDialog'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { UsageWarningBanner } from '@/components/common/UsageInfo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getDropdownOptionUsage } from '@/api/adminDropdowns'
import {
  useAdminDropdownCategories,
  useAdminDropdownOptions,
  useCreateAdminDropdownOption,
  useDeleteAdminDropdownOption,
  useReorderAdminDropdownOptions,
  useUpdateAdminDropdownOption,
} from '@/hooks/useAdminDropdowns'
import { toast } from '@/stores/useToastStore'
import type {
  DropdownAdminOption,
  DropdownOptionUsage,
} from '@/types/adminDropdowns'

interface UsageEntry {
  data?: DropdownOptionUsage
  isLoading: boolean
}

const AdminDropdowns = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')

  const categoriesQuery = useAdminDropdownCategories()
  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data]
  )

  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      setActiveCategory(categories[0])
    }
  }, [activeCategory, categories])

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [selectedOption, setSelectedOption] =
    useState<DropdownAdminOption | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DropdownAdminOption | null>(
    null
  )
  const [deleteOpen, setDeleteOpen] = useState(false)

  const [usageMap, setUsageMap] = useState<Record<string, UsageEntry>>({})
  const usageMapRef = useRef(usageMap)
  usageMapRef.current = usageMap

  const { data: options = [], isLoading } =
    useAdminDropdownOptions(activeCategory)

  const createMutation = useCreateAdminDropdownOption()
  const updateMutation = useUpdateAdminDropdownOption()
  const deleteMutation = useDeleteAdminDropdownOption()
  const reorderMutation = useReorderAdminDropdownOptions()

  const filteredOptions = useMemo(() => {
    const query = searchInput.trim().toLowerCase()
    if (!query) {
      return options
    }

    return options.filter(
      option =>
        option.value.toLowerCase().includes(query) ||
        option.display_label.toLowerCase().includes(query)
    )
  }, [options, searchInput])

  const activeCategoryLabel = activeCategory
    ? activeCategory.replace(/_/g, ' ')
    : ''

  const fetchUsage = useCallback(
    async (option: DropdownAdminOption) => {
      if (!activeCategory) return
      const key = option.value
      const current = usageMapRef.current[key]
      if (current?.data || current?.isLoading) return

      setUsageMap(prev => ({ ...prev, [key]: { isLoading: true } }))
      try {
        const data = await getDropdownOptionUsage(activeCategory, option.value)
        setUsageMap(prev => ({ ...prev, [key]: { data, isLoading: false } }))
      } catch {
        setUsageMap(prev => ({ ...prev, [key]: { isLoading: false } }))
      }
    },
    [activeCategory]
  )

  const handleEdit = (option: DropdownAdminOption) => {
    setSelectedOption(option)
    setEditOpen(true)
    fetchUsage(option)
  }

  const handleDelete = (option: DropdownAdminOption) => {
    setDeleteTarget(option)
    setDeleteOpen(true)
    fetchUsage(option)
  }

  const handleCreateSubmit = (payload: {
    value: string
    display_label: string
    display_order?: number
  }) => {
    if (!activeCategory) return
    createMutation.mutate(
      { category: activeCategory, payload },
      { onSuccess: () => setCreateOpen(false) }
    )
  }

  const handleEditSubmit = (payload: {
    value: string
    display_label: string
    display_order?: number
    is_active?: boolean
  }) => {
    if (!selectedOption || !activeCategory) return

    updateMutation.mutate(
      { category: activeCategory, id: selectedOption.id, payload },
      {
        onSuccess: () => {
          setEditOpen(false)
          setSelectedOption(null)
          setUsageMap({})
        },
      }
    )
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget || !activeCategory) return

    deleteMutation.mutate(
      { category: activeCategory, id: deleteTarget.id },
      {
        onSuccess: () => {
          setDeleteOpen(false)
          setDeleteTarget(null)
          setUsageMap({})
        },
      }
    )
  }

  const handleReorder = (
    option: DropdownAdminOption,
    direction: 'up' | 'down'
  ) => {
    if (!activeCategory) return
    if (searchInput.trim()) {
      toast.info('Clear search before reordering options.')
      return
    }

    const sourceIndex = options.findIndex(item => item.id === option.id)
    if (sourceIndex < 0) return

    const targetIndex = direction === 'up' ? sourceIndex - 1 : sourceIndex + 1
    if (targetIndex < 0 || targetIndex >= options.length) return

    const nextOrder = [...options]
    const [moved] = nextOrder.splice(sourceIndex, 1)
    nextOrder.splice(targetIndex, 0, moved)

    reorderMutation.mutate({
      category: activeCategory,
      orderedIds: nextOrder.map(item => item.id),
    })
  }

  const deleteUsage = deleteTarget ? usageMap[deleteTarget.value] : undefined
  const editUsage = selectedOption ? usageMap[selectedOption.value] : undefined

  return (
    <>
      <DropdownOptionDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        isPending={createMutation.isPending}
        onSubmit={handleCreateSubmit}
      />

      <DropdownOptionDialog
        open={editOpen}
        onOpenChange={nextOpen => {
          setEditOpen(nextOpen)
          if (!nextOpen) setSelectedOption(null)
        }}
        mode="edit"
        option={selectedOption}
        isPending={updateMutation.isPending}
        onSubmit={handleEditSubmit}
        usageBanner={
          <UsageWarningBanner
            usage={editUsage?.data}
            isLoading={editUsage?.isLoading ?? false}
          />
        }
      />

      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Deactivate Dropdown Option"
        description={`Are you sure you want to deactivate "${deleteTarget?.value ?? ''}"?`}
        confirmText="Deactivate"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      >
        <UsageWarningBanner
          usage={deleteUsage?.data}
          isLoading={deleteUsage?.isLoading ?? false}
        />
      </ConfirmationDialog>

      <div className="flex flex-col gap-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Manage Dropdown Options
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Add, edit, and manage dropdown options.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[280px,1fr]">
          <div className="rounded-lg border border-border bg-card p-3">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              Categories
            </h2>
            {categoriesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">
                Loading categories...
              </p>
            ) : categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No categories found.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {categories.map(category => {
                  const isActive = category === activeCategory

                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => {
                        setActiveCategory(category)
                        setSearchInput('')
                        setUsageMap({})
                      }}
                      className={`cursor-pointer rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium capitalize">
                          {category.replace(/_/g, ' ')}
                        </span>
                        <Badge variant={isActive ? 'info' : 'neutral'}>
                          {isActive ? options.length : '-'}
                        </Badge>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-base font-semibold capitalize text-foreground">
                    {activeCategoryLabel || 'Select a category'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Showing {filteredOptions.length} of {options.length} options
                  </p>
                </div>
                <Button type="button" onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Add Option
                </Button>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative w-full sm:max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search by value or label..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <DropdownOptionsGrid
              options={filteredOptions}
              isLoading={isLoading}
              usageMap={usageMap}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onMoveUp={option => handleReorder(option, 'up')}
              onMoveDown={option => handleReorder(option, 'down')}
              onFetchUsage={fetchUsage}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default AdminDropdowns
